import React, { useContext } from 'react';
import styled from 'styled-components';
import ThemeContext from '../contexts/ThemeContext';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';

/**
 * Shared styled components for prebooking wizard steps
 * Following DRY principle to avoid code duplication
 */

// --- Components that don't use colors (exported directly) ---

// Two-column layout: Title on left, form on right
export const TwoColumnLayout = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
`;

// Form content in right column
export const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  min-width: 0;
`;

// Navigation buttons container - Figma design
export const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ hasBackButton }) => hasBackButton ? 'space-between' : 'flex-end'};
  gap: 16px;
  margin-top: 16px;
`;

// Step container wrapper
export const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Form section container
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// --- Components with colors (wrapped with useContext) ---

// Main card container matching Figma design
const StyledCardContainer = styled.div`
  background: #ffffff;
  border: 1px solid ${({ $colors }) => $colors.borderColor};
  border-radius: 8px;
  padding: 32px 16px 16px;
  box-sizing: border-box;
`;
export const CardContainer = React.forwardRef(({ colors: _, layout: _l, ...props }, ref) => {
  const { colors } = useContext(ThemeContext);
  return <StyledCardContainer ref={ref} $colors={colors} {...props} />;
});
CardContainer.displayName = 'CardContainer';

// Section title in left column
const StyledSectionTitle = styled.h2`
  && {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.5px;
    color: ${({ $colors }) => $colors.twilight};
    margin: 0;
    min-width: 200px;
    flex-shrink: 0;
  }
`;
export const SectionTitle = React.forwardRef(({ colors: _, layout: _l, ...props }, ref) => {
  const { colors } = useContext(ThemeContext);
  return <StyledSectionTitle ref={ref} $colors={colors} {...props} />;
});
SectionTitle.displayName = 'SectionTitle';

// Divider line between sections
const StyledDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ $colors }) => $colors.borderColor};
  margin: 0;
`;
export const Divider = (props) => {
  const { colors } = useContext(ThemeContext);
  return <StyledDivider $colors={colors} {...props} />;
};

// Back navigation button - Figma design specs
const StyledBackButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
  color: ${({ $colors }) => $colors.twilight};
  background: transparent;
  border: 1px solid ${({ $colors }) => $colors.borderColorMedium};
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${({ $colors }) => $colors.hoverColor};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;
export const BackButton = (props) => {
  const { colors } = useContext(ThemeContext);
  return <StyledBackButton $colors={colors} {...props} />;
};

// Next button - Figma design specs
const StyledNextButton = styled.button`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
  color: ${({ $colors }) => $colors.buttonTextColor};
  background: ${({ $colors }) => $colors.buttonColor};
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 102px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const NextButton = ({ colors: _, ...props }) => {
  const { colors } = useContext(ThemeContext);
  return <StyledNextButton $colors={colors} {...props} />;
};

// Interpreter count banner - Figma design specs
const StyledInterpreterCountBanner = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: ${({ $bgColor }) => $bgColor};
  color: #ffffff;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;

  .interpreter-count {
    font-weight: 600;
  }

  .user-icons {
    display: flex;
    align-items: center;
    margin-right: 8px;

    svg {
      width: 20px;
      height: 20px;
    }

    svg:nth-child(2) {
      margin-left: -8px;
    }
  }
`;
export const InterpreterCountBanner = ({ colors: _, ...props }) => {
  const { colors } = useContext(ThemeContext);
  const bgColor = (colors?.brand === 'hero' || isHeroBrandEnabled())
    ? colors.heroBlue
    : colors.twilight;
  return <StyledInterpreterCountBanner $bgColor={bgColor} {...props} />;
};

// Standard input field
const StyledInput = styled.input`
  padding: 8px;
  border: 1px solid ${({ $colors }) => $colors.webDefaultInputBorderColor};
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
  width: 100%;
  height: 40px;
  background-color: ${({ $colors }) => $colors.webLightGray};
  color: ${({ $colors }) => $colors.webDarkBlue};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ $colors }) => $colors.placeholderColor};
  }

  &:focus {
    outline: none;
    border-color: ${({ $colors }) => $colors.twilight};
    color: ${({ $colors }) => $colors.twilight};
  }

  &:disabled {
    background-color: ${({ $colors }) => $colors.webLightGray};
    color: ${({ $colors }) => $colors.placeholderColor};
  }
`;
export const Input = (props) => {
  const { colors } = useContext(ThemeContext);
  return <StyledInput $colors={colors} {...props} />;
};

// Input wrapper with icon on the left
const StyledInputWithIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  .input-icon {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ $colors }) => $colors.placeholderColor};
    z-index: 1;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  input {
    padding-left: ${({ hasIcon }) => (hasIcon ? '40px' : '8px')};
    height: 40px;
  }

  .react-datepicker-wrapper {
    width: 100%;
    height: 40px;
  }

  .react-datepicker-wrapper input {
    width: 100%;
    height: 40px;
    padding: 8px;
    padding-left: ${({ hasIcon }) => (hasIcon ? '40px' : '8px')};
    border: 1px solid ${({ $colors }) => $colors.webDefaultInputBorderColor};
    border-radius: 4px;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.5px;
    line-height: 1.4;
    background-color: ${({ $colors }) => $colors.webLightGray};
    color: ${({ $colors }) => $colors.webDarkBlue};
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: ${({ $colors }) => $colors.twilight};
      color: ${({ $colors }) => $colors.twilight};
    }
  }
`;
export const InputWithIcon = (props) => {
  const { colors } = useContext(ThemeContext);
  return <StyledInputWithIcon $colors={colors} {...props} />;
};

// Standard textarea field
const StyledTextarea = styled.textarea`
  padding: 8px;
  border: 1px solid ${({ $colors }) => $colors.webDefaultInputBorderColor};
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
  width: 100%;
  min-height: 40px;
  resize: vertical;
  background-color: ${({ $colors }) => $colors.webLightGray};
  color: ${({ $colors }) => $colors.webDarkBlue};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ $colors }) => $colors.placeholderColor};
  }

  &:focus {
    outline: none;
    border-color: ${({ $colors }) => $colors.twilight};
    color: ${({ $colors }) => $colors.twilight};
  }
`;
export const Textarea = React.forwardRef((props, ref) => {
  const { colors } = useContext(ThemeContext);
  return <StyledTextarea ref={ref} $colors={colors} {...props} />;
});
Textarea.displayName = 'Textarea';

// Error message text
const StyledErrorText = styled.span`
  color: ${({ $colors }) => $colors.coral};
  font-size: 12px;
  margin-top: 4px;
`;
export const ErrorText = ({ colors: _, ...props }) => {
  const { colors } = useContext(ThemeContext);
  return <StyledErrorText $colors={colors} {...props} />;
};

// Help/hint text
const StyledHelpText = styled.p`
  font-size: 12px;
  color: ${({ $colors }) => $colors.heroPurple};
  margin: 4px 0 0 0;
  line-height: 1.2;
`;
export const HelpText = ({ colors: _, ...props }) => {
  const { colors } = useContext(ThemeContext);
  return <StyledHelpText $colors={colors} {...props} />;
};

// Required field indicator (red asterisk)
const StyledRequired = styled.span`
  color: ${({ $colors }) => $colors.coral};
`;
export const Required = ({ colors: _, ...props }) => {
  const { colors } = useContext(ThemeContext);
  return <StyledRequired $colors={colors} {...props} />;
};

// Rounded button style (for add, upload, etc.)
const StyledRoundedButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 16px;
  border: 1px solid ${({ $colors }) => $colors.borderColorMedium};
  background-color: white;
  border-radius: 20px;
  color: ${({ $colors }) => $colors.twilight};
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ $colors }) => $colors.twilight};
    background-color: ${({ $colors }) => $colors.hoverColor};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;
export const RoundedButton = React.forwardRef((props, ref) => {
  const { colors } = useContext(ThemeContext);
  return <StyledRoundedButton ref={ref} $colors={colors} {...props} />;
});
RoundedButton.displayName = 'RoundedButton';
